import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, ArrowLeft, Check, KeyRound, Mail, ShieldCheck, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

export default function Pin() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  // Normal set / verify state
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [verifyPin, setVerifyPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Fallback modes
  const [fallback, setFallback] = useState(null); // null | 'password' | 'email'
  const [pwPassword, setPwPassword] = useState("");
  const [pwNewPin, setPwNewPin] = useState("");
  const [pwConfirmPin, setPwConfirmPin] = useState("");
  const [emailStatus, setEmailStatus] = useState("idle"); // idle | loading | success | error

  if (user === null) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center">Loading…</div></div>;
  if (user === false) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center text-slate-500">Please <Link to="/auth?mode=login" className="text-brandPink font-semibold">sign in</Link></div></div>;

  const setPinAction = async (e) => {
    e.preventDefault();
    setErr("");
    if (pin.length < 4) { setErr("PIN must be at least 4 digits"); return; }
    if (pin !== confirmPin) { setErr("PINs do not match"); return; }
    setBusy(true);
    try {
      await api.post("/pin/set", { pin });
      await refresh();
      setDone(true);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  const verifyPinAction = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/pin/verify", { pin: verifyPin });
      setDone(true);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  const resetWithPasswordAction = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwNewPin.length < 4) { setErr("PIN must be at least 4 digits"); return; }
    if (pwNewPin !== pwConfirmPin) { setErr("New PINs do not match"); return; }
    setBusy(true);
    try {
      await api.post("/pin/reset-with-password", {
        email: user.email,
        password: pwPassword,
        new_pin: pwNewPin,
      });
      await refresh();
      setDone(true);
      setFallback(null);
    } catch (ex) { setErr(formatApiError(ex) || "Password incorrect. Try again."); }
    finally { setBusy(false); }
  };

  const sendPinResetEmail = async () => {
    setErr("");
    setEmailStatus("loading");
    try {
      await api.post("/auth/forgot-password", { email: user.email });
      setEmailStatus("success");
    } catch (ex) {
      setEmailStatus("error");
      setErr(formatApiError(ex) || "Could not send reset email — please try again.");
    }
  };

  const openFallback = (mode) => {
    setFallback(mode);
    setErr("");
    setEmailStatus("idle");
    setPwPassword("");
    setPwNewPin("");
    setPwConfirmPin("");
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-2xl mx-auto px-6 lg:px-8 py-10">
        <Link to="/dashboard/parent" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-brandPink" data-testid="pin-back">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="mt-4">
          <div className="pill bg-brandTeal/15 text-brandTeal"><Lock className="w-3 h-3 mr-1" /> Parent PIN</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-brandSlate mt-3" data-testid="pin-heading">
            {user.has_pin ? "Verify or change your PIN" : "Set a parent PIN"}
          </h1>
          <p className="text-slate-600 mt-2">
            Your PIN protects sensitive parent actions like approving listings and authorising payouts.
          </p>
        </div>

        {done && (
          <div className="card-base p-6 mt-6 bg-brandTeal/10 border-brandTeal/30" data-testid="pin-success">
            <div className="inline-flex items-center gap-2 text-brandTeal font-bold">
              <Check className="w-4 h-4" /> PIN saved.
            </div>
            <button onClick={() => navigate("/dashboard/parent")} className="btn-primary mt-4" data-testid="pin-success-back">
              Back to dashboard
            </button>
          </div>
        )}

        {!done && !fallback && (
          <div className="grid gap-6 mt-6">
            {!user.has_pin ? (
              /* FIRST-TIME PIN SETUP — no existing PIN, no password re-auth needed */
              <form onSubmit={setPinAction} className="card-base p-6" data-testid="pin-set-form">
                <h2 className="font-display text-xl font-semibold text-brandSlate">Create PIN</h2>
                <label className="block text-sm font-bold text-slate-700 mb-1 mt-3">New PIN (4–12 digits)</label>
                <input type="password" inputMode="numeric" pattern="\d*" required minLength={4} maxLength={12}
                  className="input-base" value={pin} onChange={(e) => setPin(e.target.value)}
                  data-testid="pin-new" />
                <label className="block text-sm font-bold text-slate-700 mb-1 mt-3">Confirm PIN</label>
                <input type="password" inputMode="numeric" pattern="\d*" required minLength={4} maxLength={12}
                  className="input-base" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)}
                  data-testid="pin-confirm" />
                <button type="submit" disabled={busy} className="btn-primary mt-4" data-testid="pin-set-btn">
                  {busy ? "Saving…" : "Set PIN"}
                </button>
              </form>
            ) : (
              /* HAS EXISTING PIN — password re-auth is REQUIRED for any reset/change.
                 The verify form stays available for read-only PIN unlock use cases. */
              <>
                <div className="card-base p-6" data-testid="pin-change-gate">
                  <div className="flex items-start gap-3">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brandTeal/15 text-brandTeal flex-shrink-0">
                      <KeyRound className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-display text-xl font-semibold text-brandSlate">Change or reset PIN</h2>
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                        For your family&rsquo;s safety, PIN resets always require your account password first.
                      </p>
                      <button
                        type="button"
                        onClick={() => openFallback("password")}
                        className="btn-teal mt-4"
                        data-testid="pin-open-password-reset"
                      >
                        <KeyRound className="w-4 h-4" /> Verify password to change PIN
                      </button>
                    </div>
                  </div>
                </div>

                <form onSubmit={verifyPinAction} className="card-base p-6" data-testid="pin-verify-form">
                  <h2 className="font-display text-xl font-semibold text-brandSlate">Verify current PIN</h2>
                  <p className="text-sm text-slate-500 mt-1">Check that your existing PIN still works.</p>
                  <input type="password" inputMode="numeric" pattern="\d*" required minLength={4} maxLength={12}
                    className="input-base mt-3" value={verifyPin} onChange={(e) => setVerifyPin(e.target.value)}
                    placeholder="Enter your PIN" data-testid="pin-verify-input" />
                  <button type="submit" disabled={busy} className="btn-secondary mt-4" data-testid="pin-verify-btn">
                    {busy ? "Verifying…" : "Verify"}
                  </button>

                  {/* Fallback options — always visible under the PIN pad */}
                  <div className="border-t border-slate-100 mt-6 pt-5">
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Can&rsquo;t remember your PIN?</div>
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => openFallback("email")}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brandPink/10 text-brandPink font-semibold text-sm hover:bg-brandPink/20 transition"
                        data-testid="pin-forgot-btn"
                      >
                        <Mail className="w-4 h-4" /> Forgot PIN?
                      </button>
                      <button
                        type="button"
                        onClick={() => openFallback("password")}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brandTeal/10 text-brandTeal font-semibold text-sm hover:bg-brandTeal/20 transition"
                        data-testid="pin-signin-with-password-btn"
                      >
                        <KeyRound className="w-4 h-4" /> Sign in with password instead
                      </button>
                    </div>
                  </div>
                </form>
              </>
            )}

            {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="pin-error">{err}</div>}
          </div>
        )}

        {/* PASSWORD RE-AUTH FALLBACK */}
        {!done && fallback === "password" && (
          <form onSubmit={resetWithPasswordAction} className="card-base p-6 md:p-7 mt-6" data-testid="pin-password-fallback">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brandTeal/15 text-brandTeal">
                <KeyRound className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold text-brandSlate">Reset PIN with account password</h2>
                <p className="text-sm text-slate-500 mt-0.5">Confirm it&rsquo;s really you, then pick a fresh PIN.</p>
              </div>
            </div>

            <label className="block text-sm font-bold text-slate-700 mb-1 mt-5">Account email</label>
            <input type="email" value={user.email} readOnly className="input-base bg-slate-50 text-slate-500" data-testid="pin-fallback-email" />

            <label className="block text-sm font-bold text-slate-700 mb-1 mt-4">Account password</label>
            <input
              type="password"
              required
              autoFocus
              value={pwPassword}
              onChange={(e) => setPwPassword(e.target.value)}
              placeholder="Your sign-in password"
              className="input-base"
              data-testid="pin-fallback-password"
              disabled={busy}
            />

            <label className="block text-sm font-bold text-slate-700 mb-1 mt-4">New PIN (4–12 digits)</label>
            <input type="password" inputMode="numeric" pattern="\d*" required minLength={4} maxLength={12}
              className="input-base" value={pwNewPin} onChange={(e) => setPwNewPin(e.target.value)}
              data-testid="pin-fallback-new-pin" disabled={busy} />

            <label className="block text-sm font-bold text-slate-700 mb-1 mt-4">Confirm new PIN</label>
            <input type="password" inputMode="numeric" pattern="\d*" required minLength={4} maxLength={12}
              className="input-base" value={pwConfirmPin} onChange={(e) => setPwConfirmPin(e.target.value)}
              data-testid="pin-fallback-confirm-pin" disabled={busy} />

            {err && <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="pin-fallback-error">{err}</div>}

            <div className="flex flex-wrap gap-3 mt-5">
              <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="pin-fallback-submit">
                {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : (<>Reset my PIN <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <button type="button" onClick={() => setFallback(null)} className="btn-secondary" data-testid="pin-fallback-cancel">
                Cancel
              </button>
            </div>

            <div className="mt-4 flex items-start gap-2 text-xs text-slate-500">
              <ShieldCheck className="w-3.5 h-3.5 text-brandTeal flex-shrink-0 mt-0.5" />
              We verify your account password with our secure sign-in system before setting the new PIN. Your existing session stays active.
            </div>
          </form>
        )}

        {/* FORGOT PIN → EMAIL RESET LINK */}
        {!done && fallback === "email" && (
          <div className="card-base p-6 md:p-7 mt-6" data-testid="pin-email-fallback">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brandPink/15 text-brandPink">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold text-brandSlate">Forgot your PIN?</h2>
                <p className="text-sm text-slate-500 mt-0.5">For safety we route PIN resets through your email.</p>
              </div>
            </div>

            {emailStatus === "success" ? (
              <div className="mt-5 rounded-2xl bg-brandTeal/10 border border-brandTeal/30 p-4 flex items-start gap-3" data-testid="pin-email-success">
                <CheckCircle2 className="w-5 h-5 text-brandTeal flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-display text-sm font-semibold text-brandSlate">Check your inbox</div>
                  <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                    We&rsquo;ve emailed <strong className="break-all">{user.email}</strong> a link to reset your account password.
                    Reset the password, sign back in, and set a new PIN from this page.
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-5 text-sm text-slate-600 leading-relaxed">
                We&rsquo;ll send a password reset link to <strong className="break-all">{user.email}</strong>.
                After you reset your password, come back here and use <em>Sign in with password instead</em> to set a new PIN — or set one from the normal PIN form.
              </div>
            )}

            {err && emailStatus !== "success" && (
              <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="pin-email-error">
                {err}
              </div>
            )}

            <div className="flex flex-wrap gap-3 mt-5">
              {emailStatus !== "success" && (
                <button
                  type="button"
                  onClick={sendPinResetEmail}
                  disabled={emailStatus === "loading"}
                  className="btn-primary disabled:opacity-60"
                  data-testid="pin-email-send-btn"
                >
                  {emailStatus === "loading" ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<>Send reset email <ArrowRight className="w-4 h-4" /></>)}
                </button>
              )}
              <button
                type="button"
                onClick={() => openFallback("password")}
                className="btn-secondary"
                data-testid="pin-email-use-password-instead"
              >
                <KeyRound className="w-4 h-4" /> Or use my password instead
              </button>
              <button type="button" onClick={() => setFallback(null)} className="btn-secondary" data-testid="pin-email-cancel">
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
